import './style.scss';

import { useMutation } from '@apollo/client';
import { Button, Fields, Link, Loader } from '@ets-global/b2c-website-ui';
import { Form, Formik } from 'formik';
import React, { Fragment, useEffect } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Trans, useTranslation } from 'react-i18next';
import { matchRoutes } from 'react-router-config';

import ErrorMessage from '../../components/Base/ErrorMessage/ErrorMessage';
import HeaderMeta from '../../components/HeaderMeta';
import FormLayout from '../../components/Layout/Form/FormLayout';
import { USER_REGISTRATION } from '../../graphql/mutations';
import { CIVILITIES, PURCHASE_ADDRESS } from '../../graphql/queries';
import { CONFIRMED_PURCHASE } from '../../graphql/queries/purchase';
import errorsHandler from '../../helpers/errorsHandler';
import { getTranslatedUserCivility } from '../../helpers/Formatter/UserFormatter';
import { getParams } from '../../helpers/router';
import { hasValidLength } from '../../helpers/Validators/PasswordValidator';
import { useAuth, useNotification, useQuery, useRouter } from '../../hooks';
import useDataLayer from '../../hooks/useDataLayer';
import useGlobalDocuments from '../../hooks/useGlobalDocuments';
import useYup from '../../hooks/useYup';
import { paths } from '../../paths';
import routes from '../../routes';

const Registration = ({ location }) => {
  const { t } = useTranslation();
  const { Yup } = useYup();
  const { newNotification } = useNotification();
  const { handleLogin } = useAuth();
  const { pushEventLoadedAccountCreationFunnel, pushEventLoginOrRegister, pushEventFormError } = useDataLayer();
  const { lang, countryEmoIso2 } = useRouter();
  const [registerUser] = useMutation(USER_REGISTRATION);
  const { data: civilitiesQueryData, loading: civilitiesQueryLoading } = useQuery(CIVILITIES);
  const { globalDocuments } = useGlobalDocuments();
  const { search } = location;
  const { referrer, registrationId: registrationIdQueryParam } = getParams(search);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const getRegistrationIdFromQueryParams = ({ referrer, registrationIdQueryParam }) => {
    if (!referrer && !registrationIdQueryParam) return null;
    if (registrationIdQueryParam) return registrationIdQueryParam;

    const matches = matchRoutes(routes, referrer);
    const route = matches.find((route) => route.match.isExact);

    if (!route || !route.match.params.registrationId) {
      return null;
    }

    return route.match.params.registrationId;
  };
  const registrationId = getRegistrationIdFromQueryParams({ referrer, registrationIdQueryParam });
  const comesFromPayment = referrer === undefined && registrationIdQueryParam;
  const { loading: addressLoading, data: addressPurchase } = useQuery(
    !comesFromPayment ? PURCHASE_ADDRESS : CONFIRMED_PURCHASE,
    {
      variables: comesFromPayment
        ? { registrationId, withBillingAddress: true, withOpenSession: false, withPurchaseLines: false }
        : { registrationId },
      skip: registrationId === null,
    }
  );

  useEffect(() => {
    pushEventLoadedAccountCreationFunnel({
      stepName: 'account creation page',
      stepNumber: '2',
    });
  }, []);

  if (addressLoading || civilitiesQueryLoading) {
    return <Loader />;
  }

  const onSubmit = async (data, actions) => {
    if (!executeRecaptcha) {
      return;
    }

    const token = await executeRecaptcha('b2c_website_registration');

    const userData = {
      civilityId: data.civilityId,
      firstname: data.firstname,
      lastname: data.lastname,
      email: data.email,
      password: data.newPassword,
      hasOptIn: Boolean(data.hasOptIn),
      countryId: countryEmoIso2.toUpperCase(),
      preferredLanguage: lang.toUpperCase(),
    };

    try {
      const {
        data: { authResult },
      } = await registerUser({ variables: { user: userData, recaptchaToken: token } });

      if (authResult.__typename === 'Error') {
        const errorMessage = t(`registration.form.error.${authResult.reason}`);
        actions.setErrors({ form: errorMessage });
        pushEventFormError(errorMessage);
      } else {
        const { user, token } = authResult;
        await handleLogin({ user, token });

        pushEventLoginOrRegister(true);
        newNotification({
          content: t('registration.message.created'),
          type: 'success',
        });
      }

      // redirection done by CantAccessLogged HOC
    } catch (error) {
      errorsHandler({
        error,
        actions,
        newNotification,
        t,
      });
    }

    actions.setSubmitting(false);
  };

  let civilities = civilitiesQueryData.civilities.map(({ id }) => ({
    value: id,
    label: getTranslatedUserCivility(id, t),
  }));
  civilities.unshift({ value: '', label: '' });

  let validations = {
    civilityId: Yup.string().required(),
    firstname: Yup.string().required(),
    lastname: Yup.string().required(),
    email: Yup.string().required().email(),
    newPassword: Yup.string()
      .test('hasValidLength', t('common.form.password.min-length'), (value) => hasValidLength(value))
      .required(),
    agreement: Yup.boolean()
      .required(t('registration.form.agreement-error-message'))
      .test('isChecked', t('registration.form.agreement-error-message'), (value) => value === true),
  };

  const UserSchema = Yup.object().shape(validations);

  const getInitialValues = (addressPurchase) => {
    let billingAddress = null;
    if (Object.keys(addressPurchase?.purchase?.billingAddress || []).length) {
      billingAddress = addressPurchase.purchase.billingAddress;
    } else if (Object.keys(addressPurchase?.confirmedPurchase?.billingAddress || []).length) {
      billingAddress = addressPurchase.confirmedPurchase.billingAddress;
    }

    return {
      civilityId: billingAddress?.civility?.id ?? '',
      firstname: billingAddress?.firstname ?? '',
      lastname: billingAddress?.lastname ?? '',
      email: billingAddress?.email ?? '',
      newPassword: '',
      hasOptIn: false,
      agreement: false,
    };
  };

  return (
    <Fragment>
      <HeaderMeta page={'user-registration'} />
      <FormLayout className={'registration-form'} title={t('registration.h1')} subtitle={t('registration.subtitle')}>
        <Formik initialValues={getInitialValues(addressPurchase)} onSubmit={onSubmit} validationSchema={UserSchema}>
          {({ isSubmitting, errors: { form } }) => (
            <Form data-cypress="registration-form">
              {form && <ErrorMessage>{form}</ErrorMessage>}
              <Fields.FormikFastField
                id={'civilityId'}
                name={'civilityId'}
                component={Fields.SelectField}
                label={t('registration.form.label.civility')}
                autoComplete={'honorific-prefix'}
                required={true}
                options={civilities}
              />
              <Fields.FormikFastField
                id={'firstname'}
                name={'firstname'}
                component={Fields.TextField}
                label={t('registration.form.label.firstname')}
                autoComplete={'given-name'}
                required={true}
              />
              <Fields.FormikFastField
                id={'lastname'}
                name={'lastname'}
                component={Fields.TextField}
                label={t('registration.form.label.lastname')}
                autoComplete={'family-name'}
                required={true}
              />
              <Fields.FormikFastField
                id={'email'}
                name={'email'}
                component={Fields.TextField}
                label={t('registration.form.label.email')}
                type={'email'}
                required={true}
                autoComplete={'email'}
              />
              <Fields.FormikFastField
                id={'newPassword'}
                name={'newPassword'}
                component={Fields.PasswordField}
                label={t('registration.form.label.password')}
                required={true}
                withStrength={true}
                autoComplete={'new-password'}
              />
              <div className="registration-form__password-tips">
                <Trans>common.form.password.length-tips</Trans>
              </div>
              <Fields.FormikFastField
                id="hasOptIn"
                name="hasOptIn"
                component={Fields.CheckboxField}
                label={t('registration.form.label.hasOptIn')}
              />
              <Fields.FormikFastField
                id="agreement"
                name="agreement"
                component={Fields.CheckboxField}
                label={
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t('registration.form.label.agreement', {
                        privacyPolicyUrl: globalDocuments.privacy,
                      }),
                    }}
                  />
                }
              />
              <Button
                className="button--full"
                disabled={isSubmitting}
                type="submit"
                aria-label={t('registration.form.label.save')}
              >
                <Trans>registration.form.label.save</Trans>
              </Button>
            </Form>
          )}
        </Formik>
        <div className="form-layout__continue">
          <Link to={paths.LOGIN} search={search} data-cypress="login-link" aria-label={t('registration.login')}>
            <Trans>registration.login</Trans>
          </Link>
        </div>

        <p className="form-layout__mentions">
          <sup>*</sup> <Trans>common.form.required.fields</Trans>
        </p>
      </FormLayout>
    </Fragment>
  );
};

export default Registration;
